import type { ObjectData } from "@novorender/data-js-api";
import { Box, spacing } from "@procore/core-react";
import { getPublicId, PUBLIC_ID_NULL_VALUE } from "novolib/utils";
import { useEffect, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useHighlighted } from "contexts/highlighted";
import { selectMainObject } from "features/render";

import { CopyButton, IdRow, Section } from "./common";

export function SelectedObjectSection() {
    const mainObject = useAppSelector(selectMainObject);
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);
    /** `null` once the lookup has resolved without an object. */
    const [objectData, setObjectData] = useState<ObjectData | null>();
    const [objectError, setObjectError] = useState<string>();

    useEffect(() => {
        setObjectData(undefined);
        setObjectError(undefined);
        if (mainObject === undefined) {
            return;
        }
        let cancelled = false;
        dataAccess.getObject(mainObject).then(
            (data) => !cancelled && setObjectData(data ?? null),
            (e: unknown) => !cancelled && setObjectError(String(e)),
        );
        return () => {
            cancelled = true;
        };
    }, [mainObject, dataAccess]);

    const publicId = objectData ? getPublicId(objectData) : undefined;

    return (
        <Section title="Selected object">
            <Box>
                <IdRow label="Internal ID" value={mainObject === undefined ? undefined : String(mainObject)} />
                <IdRow
                    label="External ID"
                    value={publicId === PUBLIC_ID_NULL_VALUE ? undefined : publicId}
                    placeholder={
                        mainObject === undefined
                            ? undefined
                            : (objectError ??
                              (objectData === undefined ? "Loading…" : objectData === null ? "Not found" : undefined))
                    }
                />
            </Box>
            <Box>
                <CopyButton
                    label="object JSON"
                    disabled={!objectData}
                    getText={() => JSON.stringify(objectData, null, 2)}
                >
                    Copy object JSON
                </CopyButton>
            </Box>
        </Section>
    );
}

export function SelectionSection() {
    const {
        state: { webviewer },
    } = useExplorerGlobals(true);
    const { idArr: highlightedIds } = useHighlighted();

    return (
        <Section title={`Selection (${highlightedIds.length})`}>
            <Box style={{ display: "flex", gap: spacing.xs }}>
                <CopyButton
                    label="selected internal IDs"
                    disabled={highlightedIds.length === 0}
                    getText={() => JSON.stringify(Array.from(highlightedIds))}
                >
                    Copy internal IDs
                </CopyButton>
                <CopyButton
                    label="selected external IDs"
                    disabled={highlightedIds.length === 0 || !webviewer}
                    getText={async () => JSON.stringify(await webviewer!.model.getSelectedObjects())}
                >
                    Copy external IDs
                </CopyButton>
            </Box>
        </Section>
    );
}
